/**
 * SessionStatusBadge — single source of truth for the colored "RSVP OPEN" /
 * "LIVE NOW" / "LOCKED" / "ENDED" pill shown on the session card.
 *
 * Phase visuals chosen for at-a-glance recognition (sports admin pattern):
 *   scheduled    → slate (calm, not actionable yet)
 *   rsvp_open    → emerald (act now)
 *   locked       → sky-blue (committed, awaiting kickoff)
 *   needs_guests → amber (something missing)
 *   live         → red (urgent / happening) with pulse — overrides admin style
 *   ended        → amber (action needed: mark attendance)
 *   completed    → muted/grey
 *   cancelled    → strikethrough rose
 */
import { formatDuration } from '../hooks/useSessionLifecycle';

const PHASE_META = {
  scheduled:    { label: 'SCHEDULED',  cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30' },
  rsvp_open:    { label: 'RSVP OPEN',  cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  locked:       { label: 'LOCKED',     cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30' },
  needs_guests: { label: 'NEEDS GUESTS', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  live:         { label: 'LIVE NOW',   cls: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40 animate-pulse' },
  ended:        { label: 'ENDED',      cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  completed:    { label: 'COMPLETED',  cls: 'bg-muted text-muted-foreground border-border' },
  cancelled:    { label: 'CANCELLED',  cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30' },
};

export default function SessionStatusBadge({ lifecycle, className = '' }) {
  if (!lifecycle) return null;
  const meta = PHASE_META[lifecycle.phase] || PHASE_META.scheduled;
  let extra = '';
  if (lifecycle.phase === 'live' && lifecycle.secondsSinceStart != null) {
    extra = ` · ${formatDuration(lifecycle.secondsSinceStart)} elapsed`;
  }
  return (
    <span
      data-testid={`session-status-badge-${lifecycle.phase}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border ${meta.cls} ${className}`}
    >
      {lifecycle.phase === 'live' && (
        <span className="relative flex h-2 w-2" aria-hidden>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
      )}
      {meta.label}{extra}
    </span>
  );
}
